import React from 'react';
import { ArrowLeft, MessageSquare, Search, FileCheck, Eye, Target, Zap, Shield, BarChart3, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrecisionRAG = () => {
  const features = [
    {
      icon: Search,
      title: "Hybrid Search (BM25 + Dense Vector)",
      description: "Combines keyword-based search with semantic understanding for comprehensive information retrieval."
    },
    {
      icon: FileCheck,
      title: "Deterministic Citation Layer",
      description: "Every response is backed by exact source references, ensuring complete traceability and auditability."
    },
    {
      icon: Eye,
      title: "Real-time Verification",
      description: "System cross-references LLM output against raw source data before user delivery."
    },
    {
      icon: Shield,
      title: "Audit Trail",
      description: "Complete logging of document, page, and paragraph used for every answer."
    }
  ];

  const workflow = [
    {
      step: "1",
      title: "Query Processing",
      description: "Natural language query is analyzed and decomposed into searchable components.",
      icon: Target,
      color: "from-blue-600 to-purple-600"
    },
    {
      step: "2",
      title: "Hybrid Retrieval",
      description: "BM25 keyword search combined with dense vector similarity search across the semantic lakehouse.",
      icon: Search,
      color: "from-green-600 to-teal-600"
    },
    {
      step: "3",
      title: "Context Assembly",
      description: "Relevant documents are ranked and context windows are prepared for LLM processing.",
      icon: FileCheck,
      color: "from-orange-600 to-red-600"
    },
    {
      step: "4",
      title: "Verified Generation",
      description: "LLM generates response with citations, followed by automated verification against sources.",
      icon: Eye,
      color: "from-purple-600 to-pink-600"
    }
  ];

  const accuracy = [
    {
      metric: "99.7%",
      description: "Factual Accuracy",
      detail: "Verified against source documents"
    },
    {
      metric: "0.3%",
      description: "Hallucination Rate",
      detail: "Industry-leading accuracy"
    },
    {
      metric: "< 2s",
      description: "Response Time",
      detail: "Including verification"
    },
    {
      metric: "100%",
      description: "Source Citations",
      detail: "Complete traceability"
    }
  ];

  const useCases = [
    {
      title: "Financial Analysis",
      description: "Extract specific financial metrics with exact citations from annual reports and filings.",
      icon: BarChart3,
      example: "What was Apple's Q3 2023 revenue from iPhone sales?"
    },
    {
      title: "Risk Assessment",
      description: "Identify and quantify risk factors with precise source references.",
      icon: Shield,
      example: "List all material risks mentioned in the 10-K filing for Q2 2023."
    },
    {
      title: "Compliance Queries",
      description: "Answer regulatory compliance questions with documented evidence.",
      icon: FileCheck,
      example: "Does our current lending practice comply with Basel III requirements?"
    },
    {
      title: "Market Research",
      description: "Synthesize market insights from multiple verified sources.",
      icon: TrendingUp,
      example: "Compare emerging market exposure across top 5 competitors."
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-navy-950 text-white">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <Link to="/" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            <div>
              <div className="inline-flex p-3 rounded-2xl bg-gradient-to-r from-[#f97316] to-[#fbce16] mb-6">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-5">
                <span className="text-gradient-blue">Precision-RAG</span>
              </h1>
              <h2 className="text-xl sm:text-2xl text-slate-300 mb-6">
                Eliminating Hallucinations through Hybrid Search
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                Standard AI often "guesses" numbers. In finance, a 1% error is a 100% failure.
                Our RAG pipeline ensures every response is backed by verified sources.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#demo"
                  className="inline-flex items-center justify-center px-7 py-3 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-[1.02]"
                >
                  Request Demo
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-7 py-3 border border-slate-700/50 text-white font-semibold rounded-lg hover:bg-white/5 transition-all duration-300"
                >
                  View All Solutions
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#f97316]/20 to-[#fbce16]/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/40 shadow-2xl shadow-orange-900/20">
                <img
                  src="/solution2_RAG.png"
                  alt="Precision RAG"
                  className="w-full h-auto object-cover max-h-[340px] sm:max-h-[420px] lg:max-h-[520px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/45 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-white">The Critical Problem</h3>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                In financial services, accuracy isn't just important—it's mandatory. 
                A single incorrect number can lead to million-dollar decisions, regulatory violations, 
                and loss of client trust. Standard LLMs hallucinate facts, figures, and citations, 
                making them unsuitable for critical financial operations.
              </p>
              <div className="bg-black/50 rounded-lg p-6 border border-red-900/50 premium-subcard premium-elevate">
                <p className="text-xl font-bold text-red-400 mb-2">The Reality:</p>
                <p className="text-2xl font-bold text-white">In finance, a 1% error is a 100% failure</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="flex items-start space-x-4">
              <MessageSquare className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">The BeeMind Solution</h3>
                <p className="text-lg text-slate-300 leading-relaxed mb-6">
                  BeeMind's RAG pipeline utilizes Hybrid Search (BM25 + Dense Vector). 
                  Every response generated by our internal chatbot is backed by a Deterministic Citation Layer.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                    <p className="text-lg font-semibold text-green-400 mb-2">Verification</p>
                    <p className="text-slate-300">The system cross-references the LLM output against the raw source in the Data Lake before the user sees it.</p>
                  </div>
                  <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                    <p className="text-lg font-semibold text-green-400 mb-2">Audit Trail</p>
                    <p className="text-slate-300">Every query logs the exact document, page, and paragraph used for the answer.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Precision-RAG Workflow</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {workflow.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative">
                    <div className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 premium-border premium-elevate hover:border-orange-500 transition-all duration-300">
                      <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${step.color} mb-4`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {step.step}
                      </div>
                      <h4 className="text-lg font-bold mb-2 text-white">{step.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed">{step.description}</p>
                    </div>
                    {index < workflow.length - 1 && (
                      <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-slate-700 transform -translate-y-1/2"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Core Features</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 hover:border-orange-500 transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="inline-flex p-3 rounded-lg bg-gradient-to-r from-orange-600 to-red-600">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold mb-2 text-white">{feature.title}</h4>
                        <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Accuracy Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {accuracy.map((stat, index) => (
                <div key={index} className="bg-navy-800 rounded-xl p-6 text-center border border-slate-700/40">
                  <p className="text-3xl font-bold text-orange-400 mb-2">{stat.metric}</p>
                  <p className="text-lg font-semibold text-white mb-1">{stat.description}</p>
                  <p className="text-sm text-slate-400">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Use Cases</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {useCases.map((useCase, index) => {
                const Icon = useCase.icon;
                return (
                  <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 hover:border-orange-500 transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="inline-flex p-3 rounded-lg bg-gradient-to-r from-orange-600 to-red-600">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold mb-2 text-white">{useCase.title}</h4>
                        <p className="text-slate-400 mb-3 leading-relaxed">{useCase.description}</p>
                        <div className="bg-black/50 rounded-lg p-3 border border-slate-700/40">
                          <p className="text-sm text-slate-400 mb-1">Example Query:</p>
                          <p className="text-sm text-orange-400 font-medium">"{useCase.example}"</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold mb-8">Achieve 100% Accuracy</h3>
            <p className="text-xl text-slate-400 mb-8">
              Eliminate hallucinations and ensure complete factual accuracy in your AI-powered financial operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#demo"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-105"
              >
                Request Demo
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center px-8 py-4 border border-slate-700/40 text-white font-semibold rounded-lg hover:bg-navy-800 transition-all duration-300"
              >
                View Other Services
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrecisionRAG;
